import { ProductStatus } from '@today/types'
import { EmptyState, Pagination, Table, type TableColumn } from '@today/ui-components'
import { Badge, Button } from '@today/ui-primitives'
import type { ReactElement, SubmitEvent } from 'react'
import { SelectInput } from './product-fields'
import type { ProductFilters, ProductOption, ProductRowView, Translate } from './product-types'

const STATUS_VARIANT: Record<ProductStatus, 'muted' | 'success' | 'secondary'> = {
	[ProductStatus.DRAFT]: 'muted',
	[ProductStatus.ACTIVE]: 'success',
	[ProductStatus.RETIRED]: 'secondary',
}

/**
 * Props for {@link ProductListView}.
 *
 * @property rows - The current page, already mapped for display.
 * @property filters - Controlled filter values (mirrored in the URL by the page hook).
 * @property empty - True when the API answered 204 for these filters.
 * @property error - True when the last request failed: renders the retry state.
 * @property onCreate - Present only when the viewer may create products.
 */
export interface ProductListViewProps {
	t: Translate
	rows: ProductRowView[]
	filters: ProductFilters
	categories: ProductOption[]
	page: number
	pageCount: number
	empty: boolean
	error: boolean
	onFilterChange: (key: keyof ProductFilters, value: string) => void
	onPageChange: (page: number) => void
	onOpen: (id: string) => void
	onRetry: () => void
	onCreate?: () => void
}

const thumbnail = (t: Translate, row: ProductRowView) =>
	row.imageUrl ? (
		<img src={row.imageUrl} alt={t('image.preview')} className="h-10 w-10 rounded-ds-md object-cover" />
	) : (
		<span role="img" aria-label={t('list.noImage')} className="block h-10 w-10 rounded-ds-md bg-ds-surface-muted" />
	)

const columns = (t: Translate, onOpen: (id: string) => void): TableColumn<ProductRowView>[] => [
	{ key: 'image', header: t('list.columns.image'), cell: (row) => thumbnail(t, row) },
	{ key: 'code', header: t('list.columns.code') },
	{
		key: 'name',
		header: t('list.columns.name'),
		cell: (row) => (
			<Button variant="muted" size="sm" onPress={() => onOpen(row.id)}>
				{row.name}
			</Button>
		),
	},
	{
		key: 'category',
		header: t('list.columns.category'),
		cell: (row) => (row.categoryName ? <Badge variant="secondary">{row.categoryName}</Badge> : '—'),
	},
	{
		key: 'status',
		header: t('list.columns.status'),
		cell: (row) => <Badge variant={STATUS_VARIANT[row.status]}>{t(`status.${row.status}`)}</Badge>,
	},
	{
		key: 'featured',
		header: t('list.columns.featured'),
		cell: (row) =>
			row.featured ? (
				<span role="img" aria-label={t('list.featured')}>
					★
				</span>
			) : null,
	},
]

function Filters({ props }: { props: ProductListViewProps }): ReactElement {
	const { t, filters, onFilterChange } = props
	const findCode = (event: SubmitEvent<HTMLFormElement>) => {
		event.preventDefault()
		onFilterChange('code', String(new FormData(event.currentTarget).get('code') ?? '').trim())
	}
	return (
		<div className="flex flex-wrap items-end gap-3">
			<SelectInput
				label={t('list.filters.status')}
				value={filters.status}
				emptyLabel={t('list.filters.any')}
				options={Object.values(ProductStatus).map((s) => ({ id: s, label: t(`status.${s}`) }))}
				onChange={(v) => onFilterChange('status', v)}
			/>
			<SelectInput
				label={t('list.filters.category')}
				value={filters.categoryId}
				emptyLabel={t('list.filters.any')}
				options={props.categories}
				onChange={(v) => onFilterChange('categoryId', v)}
			/>
			<form key={filters.code} onSubmit={findCode} className="flex items-end gap-2">
				<label className="flex flex-col gap-1.5 text-ds-sm text-ds-text-muted">
					{t('list.filters.code')}
					<input
						name="code"
						type="search"
						defaultValue={filters.code}
						className="h-10 rounded-ds-md border border-ds-border px-3"
					/>
				</label>
			</form>
		</div>
	)
}

/** The product list: filters, table with thumbnails and badges, pagination, empty and error states (organism). */
export function ProductListView(props: ProductListViewProps): ReactElement {
	const { t } = props
	let body: ReactElement
	if (props.error) {
		body = <EmptyState title={t('list.error')} action={{ label: t('actions.retry'), onPress: props.onRetry }} />
	} else if (props.empty) {
		body = <EmptyState title={t('list.empty')} />
	} else {
		body = <Table columns={columns(t, props.onOpen)} rows={props.rows} />
	}
	return (
		<section className="flex flex-col gap-4">
			<header className="flex items-center justify-between">
				<h1 className="text-ds-xl font-semibold text-ds-text">{t('list.title')}</h1>
				{props.onCreate ? <Button onPress={props.onCreate}>{t('list.new')}</Button> : null}
			</header>
			<Filters props={props} />
			{body}
			<Pagination page={props.page} pageCount={props.pageCount} onPageChange={props.onPageChange} />
		</section>
	)
}
